import { Menu, useCreatePath } from 'react-admin'
import Submenu from '@/components/Submenu'
import {
  SUBCATEGORIES,
  MAINCATEGORIES,
  PRODUCTS,
  BRANDS,
  ORDERS,
  USERS,
  PROMOCODES,
  VARIANTS,
  ADMINS,
  SETTINGS,
  ALLOWED_PAGES,
  VARIANT_COSTS,
  ROLES_AND_PERMISSIONS,
  MARKETPLACE_VARIANTS,
  COMMUNITY_CAMPAIGNS,
  CONTENTS,
  CATALOGS,
  OFFERS,
  VOUCHER_BATCHES,
  BULK_PROMO_CODES,
  PROMO_RULES,
  SUPPLIER_MAPPINGS,
  SUPPLIERS,
  CUSTOMER_CARE_BULK_MERGE,
  CUSTOMER_CARE_IDENTITY_CORRECTION,
  CUSTOMER_CARE_MERGE_ALL,
  DEEP_LINK_GENERATOR,
} from '@/services/api/routes'
import { tokens } from '@/Layout/theme'
import { CUSTOMER_CARE_ENABLED } from '@/consts/constants'

import DvrIcon from '@mui/icons-material/Dvr'
import StorefrontIcon from '@mui/icons-material/Storefront'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import LabelImportantOutlinedIcon from '@mui/icons-material/LabelImportantOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import BookOnlineOutlinedIcon from '@mui/icons-material/BookOnlineOutlined'
import AdminPanelIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import PriceCheckIcon from '@mui/icons-material/PriceCheck'
import SettingsIcon from '@mui/icons-material/Settings'
import CodeIcon from '@mui/icons-material/Code'
import CampaignIcon from '@mui/icons-material/CampaignOutlined'
import { useState, useEffect } from 'react'
import { LocalOfferOutlined, CollectionsBookmarkOutlined, BatchPredictionOutlined } from '@mui/icons-material'
import { useAbility } from '@/context/AbilityContext'
import { axiosDZAuthInstance } from '@/services/api/axios'

import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import LinkIcon from '@mui/icons-material/Link'
import BusinessIcon from '@mui/icons-material/Business'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import JoinFullIcon from '@mui/icons-material/JoinFull'
import AddLinkIcon from '@mui/icons-material/AddLink'

export const NavMenu = () => {
  const ability = useAbility()
  const createPath = useCreatePath()
  const [allowedPagesData, setAllowedPagesData] = useState<
    {
      id: string
      title: string
    }[]
  >([])

  useEffect(() => {
    axiosDZAuthInstance
      .get(`/dashboard/pages/getAdminAllowedPages`)
      .then((response) => {
        setAllowedPagesData(response.data)
      })
      .catch((error) => {
        console.error('error:', error)
      })
  }, [])

  const colors = tokens()

  // Check if user has at least one permission for System submenu
  const hasSystemPermission =
    (ability.can('findAll', 'admins') && ability.can('navbar', 'admins')) ||
    (ability.can('getUsers', 'users') && ability.can('navbar', 'users')) ||
    (ability.can('findAll', 'settings') && ability.can('navbar', 'settings')) ||
    (ability.can('getRolesPermissions', 'auth') && ability.can('navbar', 'auth'))

  // Customer Care submenu: section switched off, otherwise needs at least one permission
  const hasCustomerCarePermission =
    CUSTOMER_CARE_ENABLED &&
    (ability.can('correctIdentity', 'customer-care') ||
      ability.can('bulkMergeDuplicateAccounts', 'customer-care') ||
      ability.can('mergeAllDuplicateAccounts', 'customer-care')) &&
    ability.can('navbar', 'customer-care')

  // Check if user has at least one permission for Promo Codes submenu
  const hasPromoCodesPermission =
    (ability.can('findAll', 'promo-rules') && ability.can('navbar', 'promo-rules')) ||
    (ability.can('findAll', 'bulk-promo-codes') && ability.can('navbar', 'bulk-promo-codes')) ||
    (ability.can('findAllPromoCodes', 'promo-codes') && ability.can('navbar', 'promo-codes'))

  const styles = {
    backgroundColor: colors.neutral[0],
    '& .MuiMenuItem-root': {
      color: colors.cyanosed[700],
      borderRight: `1px solid ${colors.cyanosed[200]}`,
      fontSize: '14px',

      '&:active': {
        color: colors.primary[800],
        backgroundColor: colors.primary[100],
        fontWeight: 600,
      },
    },
    '& .RaMenuItemLink-active': {
      color: `${colors.primary[800]} !important`,
      backgroundColor: colors.primary[100],
      fontWeight: 600,
      fontSize: '14px',
      padding: 0,

      '& .RaMenuItemLink-icon': {
        color: colors.primary[800],
        marginLeft: 1.6,
      },

      '&::before': {
        content: '""',
        width: '3px',
        height: '32.97px',
        backgroundColor: colors.primary[800],
      },
    },
    marginTop: 2,
  }

  return (
    <Menu sx={styles}>
      <Menu.DashboardItem />
      {ability.can('findAll', 'brands') && ability.can('navbar', 'brands') && (
        <Menu.Item to={BRANDS()} primaryText="Brands" leftIcon={<StorefrontOutlinedIcon />} />
      )}
      {ability.can('findAll', 'products') && ability.can('navbar', 'products') && (
        <Menu.Item to={PRODUCTS()} primaryText="Products" leftIcon={<LocalMallOutlinedIcon />} />
      )}
      {ability.can('getAll', 'variants') && ability.can('navbar', 'variants') && (
        <Menu.Item to={VARIANTS()} primaryText="Variants" leftIcon={<DvrIcon />} />
      )}
      {ability.can('getMarketplaceVariants', 'marketplace-variants') &&
        ability.can('navbar', 'marketplace-variants') && (
          <Menu.Item to={MARKETPLACE_VARIANTS()} primaryText="Marketplace Variants" leftIcon={<StorefrontIcon />} />
        )}
      {ability.can('findAll', 'community-campaigns') && ability.can('navbar', 'community-campaigns') && (
        <Menu.Item to={COMMUNITY_CAMPAIGNS()} primaryText="Community Campaigns" leftIcon={<CampaignIcon />} />
      )}
      {ability.can('getAllContents', 'contents') && ability.can('navbar', 'contents') && (
        <Menu.Item to={CONTENTS()} primaryText="Contents" leftIcon={<BookOnlineOutlinedIcon />} />
      )}
      {ability.can('getAll', 'catalogs') && ability.can('navbar', 'catalogs') && (
        <Menu.Item to={CATALOGS()} primaryText="Catalogs" leftIcon={<CollectionsBookmarkOutlined />} />
      )}
      {ability.can('getAllVoucherBatches', 'voucher-batches') && ability.can('navbar', 'voucher-batches') && (
        <Menu.Item to={VOUCHER_BATCHES()} primaryText="Voucher Batches" leftIcon={<BatchPredictionOutlined />} />
      )}
      {ability.can('getAllOffers', 'offers') && ability.can('navbar', 'offers') && (
        <Menu.Item to={OFFERS()} primaryText="Offers" leftIcon={<LocalOfferOutlined />} />
      )}
      {ability.can('findAll', 'variant-costs') && ability.can('navbar', 'variant-costs') && (
        <Menu.Item to={VARIANT_COSTS()} primaryText="Variant Costs" leftIcon={<AttachMoneyIcon />} />
      )}
      {ability.can('findAll', 'main-categories') && ability.can('navbar', 'main-categories') && (
        <Menu.Item to={MAINCATEGORIES()} primaryText="Main Categories" leftIcon={<LabelOutlinedIcon />} />
      )}
      {ability.can('getAllSubcategories', 'sub-categories') && ability.can('navbar', 'sub-categories') && (
        <Menu.Item to={SUBCATEGORIES()} primaryText="Sub categories" leftIcon={<LabelImportantOutlinedIcon />} />
      )}
      {ability.can('getAllOrders', 'orders') && ability.can('navbar', 'orders') && (
        <Menu.Item to={ORDERS()} primaryText="Orders" leftIcon={<ShoppingCartOutlinedIcon />} />
      )}
      {ability.can('findAll', 'suppliers') && ability.can('navbar', 'suppliers') && (
        <Menu.Item to={SUPPLIERS()} primaryText="Suppliers" leftIcon={<BusinessIcon />} />
      )}
      {ability.can('searchMappings', 'supplier-mappings') && ability.can('navbar', 'supplier-mappings') && (
        <Menu.Item to={SUPPLIER_MAPPINGS()} primaryText="Supplier Mappings" leftIcon={<LinkIcon />} />
      )}
      {/* 
    Todo: Uncomment the following lines if we want to show the orders for merchants and suppliers
    */}
      {/* <Menu.Item to={MERCHANTS_ORDERS()} primaryText="Orders" leftIcon={<StoreOutlinedIcon />} />
    <Menu.Item to={SUPPLIERS_ORDERS()} primaryText="Orders" leftIcon={<StoreOutlinedIcon />} /> */}
      {hasPromoCodesPermission && (
        <Submenu primaryText="Promo Codes" leftIcon={<DiscountOutlinedIcon />}>
          {ability.can('findAll', 'promo-rules') && ability.can('navbar', 'promo-rules') && (
            <Menu.Item to={PROMO_RULES()} primaryText="Promo Rules" leftIcon={<PriceCheckIcon />} />
          )}

          {ability.can('findAll', 'bulk-promo-codes') && ability.can('navbar', 'bulk-promo-codes') && (
            <Menu.Item to={BULK_PROMO_CODES()} primaryText="Bulk Promo Codes" leftIcon={<DiscountOutlinedIcon />} />
          )}
          {ability.can('findAllPromoCodes', 'promo-codes') && ability.can('navbar', 'promo-codes') && (
            <Menu.Item to={PROMOCODES()} primaryText="Promo Codes" leftIcon={<LocalOfferIcon />} />
          )}
        </Submenu>
      )}
      {hasCustomerCarePermission && (
        <Submenu primaryText="Customer Care" leftIcon={<SupportAgentIcon />}>
          {ability.can('correctIdentity', 'customer-care') && (
            <Menu.Item
              to={CUSTOMER_CARE_IDENTITY_CORRECTION()}
              primaryText="User Identity Correction"
              leftIcon={<PersonSearchIcon />}
            />
          )}
          {ability.can('bulkMergeDuplicateAccounts', 'customer-care') && (
            <Menu.Item
              to={CUSTOMER_CARE_BULK_MERGE()}
              primaryText="Bulk Merge Duplicates"
              leftIcon={<CallMergeIcon />}
            />
          )}
          {ability.can('mergeAllDuplicateAccounts', 'customer-care') && (
            <Menu.Item to={CUSTOMER_CARE_MERGE_ALL()} primaryText="Merge All Duplicates" leftIcon={<JoinFullIcon />} />
          )}
        </Submenu>
      )}
      {hasSystemPermission && (
        <Submenu primaryText="System" leftIcon={<DisplaySettingsIcon />}>
          {ability.can('findAll', 'admins') && ability.can('navbar', 'admins') && (
            <Menu.Item to={ADMINS()} primaryText="Admins" leftIcon={<AdminPanelIcon />} />
          )}
          {ability.can('getUsers', 'users') && ability.can('navbar', 'users') && (
            <Menu.Item to={USERS()} primaryText="Users" leftIcon={<PeopleAltOutlinedIcon />} />
          )}
          {ability.can('findAll', 'settings') && ability.can('navbar', 'settings') && (
            <Menu.Item to={SETTINGS()} primaryText="Settings" leftIcon={<SettingsIcon />} />
          )}
          {ability.can('getRolesPermissions', 'auth') && ability.can('navbar', 'auth') && (
            <Menu.Item
              to={ROLES_AND_PERMISSIONS()}
              primaryText="Roles and permissions management"
              leftIcon={<AdminPanelIcon />}
            />
          )}
        </Submenu>
      )}

      {ability.can('navbar', 'deep-link-generator') && (
        <Menu.Item to={DEEP_LINK_GENERATOR()} primaryText="Deep Link Generator" leftIcon={<AddLinkIcon />} />
      )}
      {allowedPagesData &&
        allowedPagesData?.map((pageData) => (
          <Menu.Item
            to={createPath({ resource: ALLOWED_PAGES(pageData.id), type: 'list', id: pageData.id })}
            primaryText={`${pageData.title}`}
            key={pageData.id}
            leftIcon={<CodeIcon />}
          />
        ))}
    </Menu>
  )
}
